import { NextSeo } from "next-seo"; 
import FormInput from '@c/forms/controls/FormInput';
import FormTextarea from '@c/forms/controls/FormTextarea';
import FormLabel from '@c/forms/controls/FormLabel';
import { DefaultLayout } from '@c/layouts';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@ui/Button';
import { contactUs } from '@util/firestore/messages';
import { useAuth } from 'context/AuthContext';
import { ReactElement, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useToastContext } from 'context/ToastContext';
import DynamicToast from '@c/toasts/DynamicToast';
import HelpCenterHeader from '@c/headers/helpcenter/HelpCenterHeader';
import { EmailFeatureIcon, SmartphoneIcon } from '@c/icons';
import ChatModal from '@c/modals/ChatModal';
import Link from 'next/link';
import { logError } from '@util/logError';

const HelpCategoryCard = ({
  title,
  description,
  onClick,
  icon,
  buttonText,
  href,
  text,
}: {
  title: string;
  description: string;
  onClick?: () => void;
  href?: string;
  icon: React.ReactElement;
  buttonText?: string;
  text?: string;
}) => (
  <div className="flex w-full grow flex-col items-center gap-[3.2rem] rounded-[1rem] border-[1px] p-[4rem]">
    {icon}
    <div className="flex flex-col items-center gap-[1.2rem]">
      <p className="text-center text-[2.4rem] font-semibold leading-[3.2rem]">
        {title}
      </p>
      <p className="text-center text-[1.6rem] leading-[2.4rem]">
        {description}
      </p>
    </div>
    {buttonText && (
      <Button
        type="secondary"
        onClick={onClick}
        text={buttonText}
        href={href}
      />
    )}
    {text && (
      <Link
        href="tel:8776063504"
        className="text-center text-[2.4rem] font-semibold leading-[2.4rem] text-brand-secondary"
      >
        {text}
      </Link>
    )}
  </div>
);

const checkoutInfoFormSchema = z.object({
  email: z.string().email(),
  name: z.string(),
  message: z.string(),
  phone: z.string(),
});

export type ContactUsForm = z.infer<typeof checkoutInfoFormSchema>;

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const { userDoc } = useAuth();
  const { showToast } = useToastContext();
  const {
    reset,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContactUsForm>({
    resolver: zodResolver(checkoutInfoFormSchema),
  });

  useEffect(() => {
    if (userDoc?.email) setValue('email', userDoc?.email);
    if (userDoc?.name) setValue('name', userDoc?.name);
  }, [userDoc, setValue]);

  async function onSubmit(data: ContactUsForm) {
    try {
      if (data.message) {
        setLoading(true);
        await contactUs(data);
        setLoading(false);
        showToast(<DynamicToast text={'Message sent'} error />, {
          position: 'bottom-center',
          style: {
            borderRadius: '10px',
            backgroundColor: '#FDF4E9',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: '#C75008',
            width: 'min-content',
          },
        });
        reset();
      }
    } catch (e: any) {
      logError(e);
      showToast(<DynamicToast text={e.message || 'Failed to send'} />, {
        position: 'bottom-center',
        style: {
          borderRadius: '10px',
          backgroundColor: '#FDF4E9',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: '#C75008',
          width: 'min-content',
        },
      });
    }
  }
  const [showForm, setShowForm] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  
  return (
    <>
      <NextSeo
        title="Contact Us - GearFocus: Buy & Sell Used Camera Gear"
        description="Need help with buying or selling on GearFocus? Contact us today! We’re here to assist with all your questions about used camera gear, lighting, sound, and computer equipment."
        openGraph={{
          images: [
            {
              url: "https://gearfocus.com/gear_pile.png",
              width: 800,
              height: 600,
              alt: "Contact Us - GearFocus: Buy & Sell Used Camera Gear",
              type: "image/jpeg",
            },
          ],
          siteName: "GearFocus Marketplace",
          type: 'website',
          url: "https://gearfocus.com/contact-us",
        }}
      />
    
      <div className="w-full">
        <ChatModal
          isOpen={showChatModal}
          dismiss={() => setShowChatModal(false)}
          recipientUid={process.env.NEXT_PUBLIC_SUPPORT_ID ?? ''}
        />
        <HelpCenterHeader />

        <div className="mx-auto flex max-w-[144rem] flex-col gap-[6rem] px-[2rem] py-[6rem] sm:px-[12rem]">
          <div className="flex flex-col items-center gap-[1.6rem]">
            <h2 className="text-center text-[3.2rem] font-semibold leading-[4.2rem]">
              Contact Us
            </h2>
            <p className="text-center text-[1.5rem] leading-[2.2rem]">
              You can find answers to most questions in a few seconds on Help
              Center, or reach out anytime with the methods below when you need
              additional help.
            </p>
          </div>
          {!showForm ? (
            <div className="grid gap-[2.4rem] sm:grid-cols-2">
              {/* <HelpCategoryCard
                title="Chat with us"
                description="If you have a GearFocus account, chat with our customer support available 9am-4pm EST."
                onClick={() => {
                  if (userDoc) {
                    setShowChatModal(true);
                  } else {
                    router.push('/login?redirect=/contact-us');
                  }
                }}
                icon={<ChattingAppIcon />}
                buttonText="Chat Now"
              /> */}
              <HelpCategoryCard
                title="Send us an email"
                description="If you need additional help, please reach out to us via email."
                icon={<EmailFeatureIcon />}
                buttonText="Send Email"
                onClick={() => setShowForm(true)}
                // href="mailto:support@gearfocus.com"
              />
              <HelpCategoryCard
                title="Call us"
                description="Our team is available to answer your calls and provide the answers."
                onClick={() => undefined}
                icon={<SmartphoneIcon />}
                text="877-606-3504"
              />
            </div>
          ) : (
            <form
              className="mx-auto mt-[3rem] flex w-full max-w-[40rem] flex-col gap-16"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormLabel value="Email" required>
                <FormInput
                  type="email"
                  placeholder="Enter your email"
                  error={!!errors.email}
                  {...register('email')}
                  autoComplete="email"
                />
              </FormLabel>
              <FormLabel value="Name" required>
                <FormInput
                  type="text"
                  placeholder="Enter your name"
                  error={!!errors.name}
                  {...register('name')}
                  autoComplete="name"
                />
              </FormLabel>
              <FormLabel value="Phone" required>
                <FormInput
                  type="tel"
                  placeholder="Enter your phone number"
                  error={!!errors.phone}
                  {...register('phone')}
                  autoComplete="tel"
                />
              </FormLabel>
              <FormLabel value="Message" required>
                <FormTextarea {...register('message')} error={!!errors.message} />
              </FormLabel>
              <div className="mx-auto flex flex-col justify-between gap-4 sm:flex-row">
                <Button
                  text="Send Email"
                  type="secondary"
                  buttonType="submit"
                  loading={loading}
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

ContactUs.getLayout = function getLayout(page: ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};
export default ContactUs;
